import {
  Button,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { ArtistLink } from '@wwaave/db/schema';
import { ART_FORMS, COUNTRIES } from '@wwaave/shared/vocab';
import { useEffect, useRef } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { ArtistGalleryField, ArtistPhotoField } from '../../components/profile/ArtistImageFields';
import { ArtistLinksField } from '../../components/profile/ArtistLinksField';
import { GenresField } from '../../components/profile/GenresField';
import { EmptyState, MotionSection, PageHero } from '../../components/ui';
import { csrfHeaders, useAuth } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

/**
 * /profile/edit — own artist profile edit form.
 *
 * CRITICAL: every multi-select pre-populates from the saved record. The
 * Mantine form is initialised with empty defaults at mount, then `setValues()`
 * replaces them once the GET resolves. This is the explicit fix for the
 * audit's "Edit form wipes saved values" bug class — saving a form whose
 * art_forms field hasn't been initialised would clear the m2m table.
 */

type ArtistProfileResponse = {
  ok: true;
  profile: {
    id: string;
    slug: string;
    bio: string | null;
    city: string | null;
    country: string | null;
    discipline_summary: string | null;
    profile_photo_url: string | null;
    party_size_default: number | null;
    dietary_notes: string | null;
    sleeping_preferences: string | null;
    accessibility_needs: string | null;
    published: boolean;
    links?: ArtistLink[];
    gallery_image_urls?: string[];
    genres?: string[];
  };
  art_forms: string[];
};

type ApiError = { ok: false; error: { code: string; message: string } };

type FormValues = {
  bio: string;
  city: string;
  country: string;
  discipline_summary: string;
  art_forms: string[];
  genres: string[];
  links: ArtistLink[];
  party_size_default: number | '';
  dietary_notes: string;
  sleeping_preferences: string;
  accessibility_needs: string;
  published: boolean;
};

const EMPTY_VALUES: FormValues = {
  bio: '',
  city: '',
  country: '',
  discipline_summary: '',
  art_forms: [],
  genres: [],
  links: [],
  party_size_default: '',
  dietary_notes: '',
  sleeping_preferences: '',
  accessibility_needs: '',
  published: false,
};

const HTTPS_URL_RE = /^https:\/\/[^\s]+$/i;

async function fetchArtistProfile(): Promise<ArtistProfileResponse | ApiError> {
  const r = await fetch('/api/profiles/me/artist', { credentials: 'include' });
  return (await r.json()) as ArtistProfileResponse | ApiError;
}

async function saveArtistProfile(values: Record<string, unknown>): Promise<unknown> {
  const r = await fetch('/api/profiles/me/artist', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
    body: JSON.stringify(values),
  });
  const j = await r.json();
  if (!r.ok) throw new Error((j as ApiError).error?.message ?? 'Save failed.');
  return j;
}

function ProfileEdit() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const profileQ = useQuery({
    queryKey: ['profile', 'me', 'artist'],
    queryFn: fetchArtistProfile,
  });

  const form = useForm<FormValues>({
    initialValues: EMPTY_VALUES,
    validate: {
      bio: (v) => (v.length > 2000 ? 'Bio must be under 2000 characters.' : null),
      art_forms: (v) =>
        v.length === 0 ? 'Pick at least one art form.' : null,
      links: (v) => {
        for (const link of v) {
          if (link.url.trim().length === 0) return 'Every link needs a URL.';
          if (!HTTPS_URL_RE.test(link.url)) {
            return 'Links must start with https://. The rest of the URL is up to you.';
          }
        }
        return null;
      },
    },
  });

  // Pre-populate as soon as the GET resolves. This is the critical step.
  // Once only: the photo + gallery fields refresh this query as they save, and
  // re-filling the form then would throw away whatever is typed but unsaved.
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    if (!profileQ.data || !profileQ.data.ok) return;
    hydrated.current = true;
    const { profile, art_forms } = profileQ.data;
    form.setValues({
      bio: profile.bio ?? '',
      city: profile.city ?? '',
      country: profile.country ?? '',
      discipline_summary: profile.discipline_summary ?? '',
      art_forms,
      genres: profile.genres ?? [],
      links: profile.links ?? [],
      party_size_default: profile.party_size_default ?? '',
      dietary_notes: profile.dietary_notes ?? '',
      sleeping_preferences: profile.sleeping_preferences ?? '',
      accessibility_needs: profile.accessibility_needs ?? '',
      published: profile.published,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileQ.data]);

  const saveM = useMutation({
    mutationFn: saveArtistProfile,
    onSuccess: async () => {
      // What's in the form is now what's saved.
      form.resetDirty();
      await qc.invalidateQueries({ queryKey: ['profile', 'me', 'artist'] });
      showToast({ message: 'Saved.', variant: 'success' });
    },
    onError: (err: Error) => {
      showToast({ message: err.message, variant: 'error' });
    },
  });

  if (profileQ.isPending) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }
  if (!profileQ.data || !profileQ.data.ok) {
    return (
      <AppShell tone="form">
        <EmptyState
          title="Your profile isn't set up yet."
          body="Finish onboarding before editing."
          action={
            <Button component={Link} to="/onboarding/artist">
              Continue onboarding
            </Button>
          }
        />
      </AppShell>
    );
  }

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Edit"
            title="Edit profile"
            lede="Saved changes go live immediately. Hosts read this before they reply."
            action={
              <Button component={Link} to="/profile" variant="subtle">
                Back to preview
              </Button>
            }
          />
        </MotionSection>

        {/* Photo + gallery save on their own (upload -> the server writes the
            row), so they sit outside the form: its Save would otherwise send a
            stale copy and undo a fresh upload. */}
        <Paper p="md" withBorder radius="md">
          <Stack gap="lg">
            <Stack gap="xs">
              <Title order={3}>Photo</Title>
              <Text c="dimmed" size="sm">
                A clear photo of you. One good one beats five blurry ones. Saves as soon as it
                uploads.
              </Text>
              <ArtistPhotoField
                name={user?.displayName ?? ''}
                value={profileQ.data.profile.profile_photo_url}
                onChange={() => void qc.invalidateQueries({ queryKey: ['profile', 'me', 'artist'] })}
              />
            </Stack>
            <Stack gap="xs">
              <Title order={3}>Gallery</Title>
              <Text c="dimmed" size="sm">
                Performance shots, press photos, your studio. Up to 12. The first one leads.
              </Text>
              <ArtistGalleryField
                value={profileQ.data.profile.gallery_image_urls ?? []}
                onChange={(next) =>
                  qc.setQueryData<ArtistProfileResponse | ApiError>(
                    ['profile', 'me', 'artist'],
                    (prev) =>
                      prev !== undefined && prev.ok
                        ? { ...prev, profile: { ...prev.profile, gallery_image_urls: next } }
                        : prev,
                  )
                }
              />
            </Stack>
          </Stack>
        </Paper>

        <form
          onSubmit={form.onSubmit((values) => {
            saveM.mutate(toPatchPayload(values));
          })}
        >
          <Stack gap="lg">
            <Paper p="md" withBorder radius="md">
              <Stack gap="md">
                <Title order={3}>Basics</Title>
                <TextInput
                  label="One-line summary"
                  description="What do you bring to a room? Hosts read this first."
                  placeholder="Acoustic singer-songwriter, two voices and a guitar"
                  maxLength={160}
                  {...form.getInputProps('discipline_summary')}
                />
                <Textarea
                  label="Bio"
                  description="Three short paragraphs work better than one long one."
                  placeholder=""
                  autosize
                  minRows={4}
                  maxRows={12}
                  {...form.getInputProps('bio')}
                />
                <Group grow>
                  <TextInput label="City" placeholder="Brisbane" {...form.getInputProps('city')} />
                  <Select
                    label="Country"
                    placeholder="Pick one"
                    data={COUNTRIES.filter((c) => c.enabled).map((c) => ({
                      value: c.code,
                      label: c.name,
                    }))}
                    clearable
                    {...form.getInputProps('country')}
                  />
                </Group>
              </Stack>
            </Paper>

            <Paper p="md" withBorder radius="md">
              <Stack gap="md">
                <Title order={3}>What you do</Title>
                <MultiSelect
                  label="Art forms"
                  description="Pick all that fit. Hosts filter by these."
                  data={ART_FORMS.map((a) => ({ value: a.slug, label: a.name }))}
                  searchable
                  required
                  {...form.getInputProps('art_forms')}
                />
                <div>
                  <Text fw={500} size="sm" mb={4}>
                    Genres
                  </Text>
                  <Text c="dimmed" size="xs" mb={6}>
                    Up to eight. Type and press enter.
                  </Text>
                  <GenresField
                    value={form.values.genres}
                    onChange={(next) => form.setFieldValue('genres', next)}
                    error={form.errors['genres']}
                  />
                </div>
              </Stack>
            </Paper>

            <Paper p="md" withBorder radius="md">
              <Stack gap="md">
                <Title order={3}>Links</Title>
                <Text c="dimmed" size="sm">
                  Where you live online. Spotify, Bandcamp, Instagram, your own site.
                </Text>
                <ArtistLinksField
                  value={form.values.links}
                  onChange={(next) => form.setFieldValue('links', next)}
                  error={form.errors['links']}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder radius="md">
              <Stack gap="md">
                <Title order={3}>On the road</Title>
                <NumberInput
                  label="Default party size"
                  description="Including you. Used to pre-fill request forms."
                  min={1}
                  max={50}
                  {...form.getInputProps('party_size_default')}
                />
                <Textarea
                  label="Dietary notes"
                  placeholder="Vegetarian, gluten-free, etc."
                  autosize
                  minRows={2}
                  {...form.getInputProps('dietary_notes')}
                />
                <Textarea
                  label="Sleeping preferences"
                  placeholder="Quiet room, willing to camp, etc."
                  autosize
                  minRows={2}
                  {...form.getInputProps('sleeping_preferences')}
                />
                <Textarea
                  label="Accessibility needs"
                  placeholder=""
                  autosize
                  minRows={2}
                  {...form.getInputProps('accessibility_needs')}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder radius="md">
              <Stack gap="md">
                <Title order={3}>Visibility</Title>
                <Switch
                  label="Published"
                  description="Hosts can find your profile when this is on."
                  {...form.getInputProps('published', { type: 'checkbox' })}
                />
              </Stack>
            </Paper>

            <Group justify="flex-end">
              <Button type="button" variant="subtle" onClick={() => navigate({ to: '/profile' })}>
                Cancel
              </Button>
              <Button type="submit" loading={saveM.isPending} disabled={!form.isDirty()}>
                Save changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </AppShell>
  );
}

function toPatchPayload(v: FormValues): Record<string, unknown> {
  // Only send fields the form actually populated. Empty strings are sent as
  // empty strings (intentional clears); empty arrays for art_forms are not
  // sent because the validator will reject min(1) — the form blocks submit.
  const payload: Record<string, unknown> = {
    bio: v.bio,
    city: v.city,
    country: v.country.length === 2 ? v.country : undefined,
    discipline_summary: v.discipline_summary,
    art_forms: v.art_forms,
    genres: v.genres,
    links: v.links,
    party_size_default: v.party_size_default === '' ? undefined : v.party_size_default,
    dietary_notes: v.dietary_notes,
    sleeping_preferences: v.sleeping_preferences,
    accessibility_needs: v.accessibility_needs,
    published: v.published,
  };
  // Strip undefined keys before sending — the server treats absence as "no change"
  // but explicit undefined would JSON-stringify to nothing anyway; this keeps the
  // wire payload tidy.
  for (const k of Object.keys(payload)) {
    if (payload[k] === undefined) delete payload[k];
  }
  return payload;
}

export const Route = createFileRoute('/profile/edit')({
  component: ProfileEdit,
});
